import React, { useState } from 'react';
import { Grid3X3, User, Heart } from 'lucide-react';

import GradientButtonTab from '@/components/ui/GradientButtonTab';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import GlassPill from '@/components/ui/GlassPill';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface VideoLibraryHeaderProps {
  showOnlyUserVideos: boolean;
  showFavorites: boolean;
  onSetShowOnlyUserVideos: (value: boolean) => void;
  onSetShowFavorites: (value: boolean) => void;
  isLoading?: boolean;
}

export const VideoLibraryHeader: React.FC<VideoLibraryHeaderProps> = ({
  showOnlyUserVideos,
  showFavorites,
  onSetShowOnlyUserVideos,
  onSetShowFavorites,
  isLoading = false
}) => {
  const { isMobile } = useIsMobile();
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);

  const handleFilterChange = (showUser: boolean, showFavs: boolean) => {
    onSetShowOnlyUserVideos(showUser);
    onSetShowFavorites(showFavs);
  };

  const handleMobileTooltip = (tooltipId: string) => {
    if (isMobile) {
      setOpenTooltip(tooltipId);
      setTimeout(() => setOpenTooltip(null), 1000); // Show for 1 second
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex space-x-2 flex-1 md:flex-initial">
          <TooltipProvider delayDuration={isMobile ? 0 : 300}>
            <Tooltip open={isMobile ? openTooltip === 'all' : undefined}>
              <TooltipTrigger asChild>
                <GradientButtonTab
                  variant="purple"
                  isActive={!showOnlyUserVideos && !showFavorites}
                  onClick={() => {
                    handleFilterChange(false, false);
                    handleMobileTooltip('all');
                  }}
                  icon={<Grid3X3 className="h-4 w-4" />}
                  testId="all-videos-button"
                  fullWidth={true}
                  className="md:w-auto"
                >
                  All Videos
                </GradientButtonTab>
              </TooltipTrigger>
              <TooltipContent className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border border-white/20 shadow-lg">
                View All Videos
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider delayDuration={isMobile ? 0 : 300}>
            <Tooltip open={isMobile ? openTooltip === 'user' : undefined}>
              <TooltipTrigger asChild>
                <GradientButtonTab
                  variant="blue"
                  isActive={showOnlyUserVideos}
                  onClick={() => {
                    handleFilterChange(true, false);
                    handleMobileTooltip('user');
                  }}
                  icon={<User className="h-4 w-4" />}
                  testId="your-videos-button"
                  fullWidth={true}
                  className="md:w-auto"
                >
                  Your Videos
                </GradientButtonTab>
              </TooltipTrigger>
              <TooltipContent className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border border-white/20 shadow-lg">
                View Your Creations
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider delayDuration={isMobile ? 0 : 300}>
            <Tooltip open={isMobile ? openTooltip === 'favorites' : undefined}>
              <TooltipTrigger asChild>
                <GradientButtonTab
                  variant="red"
                  isActive={showFavorites}
                  onClick={() => {
                    handleFilterChange(false, true);
                    handleMobileTooltip('favorites');
                  }}
                  icon={<Heart className="h-4 w-4" />}
                  testId="favorites-button"
                  fullWidth={true}
                  className="md:w-auto"
                >
                  Favorites
                </GradientButtonTab>
              </TooltipTrigger>
              <TooltipContent className="bg-gradient-to-br from-red-500 to-red-600 text-white border border-white/20 shadow-lg">
                View Your Favorites
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Header/Loading indicator - Hidden on mobile */}
        <div 
          className="hidden md:flex items-center" 
          data-test-id="library-header-indicator"
        >
          <div className={cn(isLoading && "animate-pulse")}>
            <GlassPill
              type={isLoading 
                ? "Loading..." 
                : showOnlyUserVideos 
                  ? "Your Creations" 
                  : showFavorites 
                    ? "Your Favorites"
                    : "All Videos"}
              variant="purple"
              className="text-sm font-medium bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent border-purple-200/50 dark:border-purple-800/30"
            />
          </div>
        </div>
      </div>
      
      
    </>
  );
};

export default VideoLibraryHeader; 